// frontend/src/services/project.service.ts
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';

export interface Project {
  id: string;
  projectCode: string;
  projectName: string;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface BulkUploadResult {
  success: boolean;
  message: string;
  uploaded: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: string[];
  details: {
    uploaded: Array<{ code: string; name: string }>;
    updated: Array<{ code: string; oldName: string; newName: string }>;
    skipped: Array<{ code: string; name: string; reason: string }>;
    failed: Array<{ code: string; name: string; error: string }>;
  };
}

class ProjectService {
  private cache: Project[] | null = null;
  private lastFetchTime: number = 0;
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  /**
   * Generate unique document ID from project code AND name
   * This allows multiple projects with same code but different names
   */
  private generateDocId(projectCode: string, projectName: string): string {
    // Combine code and name to create unique ID
    const combined = `${projectCode}_${projectName}`;
    // Sanitize: remove special characters, replace spaces with underscores
    return combined
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .substring(0, 200); // Firestore doc ID limit
  }

  /**
   * Bulk upload projects - allows same code with different names
   */
  async bulkUploadProjects(
    projectData: Array<{ projectCode: string; projectName: string }>,
    options: {
      updateExisting?: boolean;
      skipDuplicates?: boolean;
    } = {}
  ): Promise<BulkUploadResult> {
    const { updateExisting = true, skipDuplicates = false } = options;

    try {
      console.log(`📤 Starting bulk upload of ${projectData.length} projects...`);
      console.log(`✨ Multiple projects with same code are ALLOWED`);
      
      const batch = writeBatch(db);
      const errors: string[] = [];
      const details = {
        uploaded: [] as Array<{ code: string; name: string }>,
        updated: [] as Array<{ code: string; oldName: string; newName: string }>,
        skipped: [] as Array<{ code: string; name: string; reason: string }>,
        failed: [] as Array<{ code: string; name: string; error: string }>
      };

      let uploaded = 0;
      let updated = 0;
      let skipped = 0;
      let failed = 0;

      // Track code+name combinations we've seen in this batch
      const seenInBatch = new Set<string>();

      // Process each project
      for (const project of projectData) {
        try {
          // Validate data
          if (!project.projectCode || !project.projectName) {
            const error = `Invalid project data: missing code or name`;
            errors.push(error);
            details.failed.push({
              code: project.projectCode || 'UNKNOWN',
              name: project.projectName || 'UNKNOWN',
              error
            });
            failed++;
            continue;
          }

          // Clean the data
          const cleanCode = project.projectCode.trim().toUpperCase();
          const cleanName = project.projectName.trim();

          // Create unique key from code + name
          const uniqueKey = `${cleanCode}___${cleanName}`;

          // Check for exact duplicates (same code AND same name) in current batch
          if (seenInBatch.has(uniqueKey)) {
            console.warn(`⚠️ Exact duplicate in batch: ${cleanCode} - ${cleanName}`);
            details.skipped.push({
              code: cleanCode,
              name: cleanName,
              reason: 'Exact duplicate (same code and name) in upload file'
            });
            skipped++;
            continue;
          }

          // Mark this code+name combination as seen
          seenInBatch.add(uniqueKey);

          // Generate document ID from BOTH code and name
          const docId = this.generateDocId(cleanCode, cleanName);
          const projectRef = doc(db, 'projects', docId);

          // Check if this exact project (same code + name) already exists
          const existingDoc = await getDoc(projectRef);
          
          if (existingDoc.exists()) {
            // Exact same project already exists - skip
            console.log(`⏭️  Skipping identical project: ${cleanCode} - ${cleanName}`);
            details.skipped.push({
              code: cleanCode,
              name: cleanName,
              reason: 'Already exists with exact same code and name'
            });
            skipped++;
            continue;
          }

          // New project - add it (even if code exists with different name)
          batch.set(projectRef, {
            projectCode: cleanCode,
            projectName: cleanName,
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            metadata: {
              source: 'excel_import',
              importDate: new Date().toISOString(),
              docId: docId
            }
          });

          console.log(`✅ Prepared new project: ${cleanCode} - ${cleanName}`);
          details.uploaded.push({
            code: cleanCode,
            name: cleanName
          });
          uploaded++;

        } catch (error) {
          console.error(`❌ Error processing project:`, project, error);
          const errorMsg = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Failed to process ${project.projectCode} - ${project.projectName}: ${errorMsg}`);
          details.failed.push({
            code: project.projectCode,
            name: project.projectName,
            error: errorMsg
          });
          failed++;
        }
      }

      // Commit the batch if there are changes
      if (uploaded > 0) {
        await batch.commit();
        console.log(`✅ Successfully committed ${uploaded} projects to Firebase`);
      } else {
        console.log(`ℹ️  No new projects to add`);
      }

      // Clear cache after upload
      this.clearCache();

      // Determine overall success
      const overallSuccess = failed === 0;
      
      // Build detailed message
      let message = '';
      if (uploaded > 0) message += `${uploaded} new project(s) added. `;
      if (skipped > 0) message += `${skipped} project(s) skipped (already exist). `;
      if (failed > 0) message += `${failed} project(s) failed. `;
      
      if (!message) {
        message = 'No changes made - all projects already exist.';
      }

      return {
        success: overallSuccess,
        message: message.trim(),
        uploaded,
        updated,
        skipped,
        failed,
        errors,
        details
      };

    } catch (error) {
      console.error('❌ Bulk upload failed:', error);
      return {
        success: false,
        message: `Bulk upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        uploaded: 0,
        updated: 0,
        skipped: 0,
        failed: projectData.length,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        details: {
          uploaded: [],
          updated: [],
          skipped: [],
          failed: projectData.map(p => ({
            code: p.projectCode,
            name: p.projectName,
            error: 'Batch operation failed'
          }))
        }
      };
    }
  }

  /**
   * Get all active projects for dropdown
   */
  async getActiveProjects(): Promise<Project[]> {
    try {
      // Check cache first
      if (this.cache && (Date.now() - this.lastFetchTime) < this.CACHE_TTL) {
        console.log('📦 Returning cached projects');
        return this.cache;
      }

      console.log('🔄 Fetching projects from Firebase...');
      
      const projectsQuery = query(
        collection(db, 'projects'),
        where('isActive', '==', true),
        orderBy('projectCode', 'asc'),
        orderBy('projectName', 'asc')
      );

      const snapshot = await getDocs(projectsQuery);
      
      const projects: Project[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          projectCode: data.projectCode,
          projectName: data.projectName,
          isActive: data.isActive,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });
      });

      // Update cache
      this.cache = projects;
      this.lastFetchTime = Date.now();

      console.log(`✅ Fetched ${projects.length} active projects`);
      return projects;

    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      throw error;
    }
  }

  /**
   * Search projects by code or name
   */
  async searchProjects(searchTerm: string): Promise<Project[]> {
    try {
      const allProjects = await this.getActiveProjects();
      const search = searchTerm.toLowerCase().trim();
      
      return allProjects.filter(project => 
        project.projectCode.toLowerCase().includes(search) ||
        project.projectName.toLowerCase().includes(search)
      );
    } catch (error) {
      console.error('❌ Error searching projects:', error);
      return [];
    }
  }

  /**
   * Get all projects by code (can return multiple projects)
   */
  async getProjectsByCode(projectCode: string): Promise<Project[]> {
    try {
      const allProjects = await this.getActiveProjects();
      return allProjects.filter(p => p.projectCode === projectCode.toUpperCase());
    } catch (error) {
      console.error('❌ Error getting projects by code:', error);
      return [];
    }
  }

  /**
   * Get a single project by code and name
   */
  async getProjectByCodeAndName(projectCode: string, projectName: string): Promise<Project | null> {
    try {
      const allProjects = await this.getActiveProjects();
      return allProjects.find(p => 
        p.projectCode === projectCode.toUpperCase() && 
        p.projectName === projectName.trim()
      ) || null;
    } catch (error) {
      console.error('❌ Error getting project by code and name:', error);
      return null;
    }
  }

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<{ success: boolean; message: string }> {
    try {
      const projectRef = doc(db, 'projects', projectId);
      await setDoc(projectRef, { isActive: false, updatedAt: serverTimestamp() }, { merge: true });
      
      this.clearCache();
      
      return {
        success: true,
        message: 'Project deactivated successfully'
      };
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to delete project'
      };
    }
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache = null;
    this.lastFetchTime = 0;
    console.log('🧹 Project cache cleared');
  }
}

export const projectService = new ProjectService();
